import React, { memo } from 'react'
import ClockIcon from 'transfix-icons/build/ClockIcon'
import { Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IFacilityScheduleRulesUnloadTypes, IShipmentStopTypes } from 'types/graphqlTypes'
import { IAwardedLaneFragment } from 'apps/marketplace/pages/awarded-lanes/graphql/AwardedLaneFragment'
import { titleCase } from 'utils/titleCase'
import { DISPLAY } from 'constants/display'
import { getAwardedLaneFacilityShiftRows, isFacilityOpen247 } from '../helpers'

const useAwardedLaneFacilityDetailsStyles = makeStyles({
  container: {
    width: 250,
    maxWidth: 250,
  },
  clockIcon: {
    color: brandColors.coolGray5,
  },
  hoursText: {
    padding: 0,
  },
})

interface ILaneFacilityDetailsProps {
  dataTest?: string
  shipperRateShipperFacilities: IAwardedLaneFragment['shipperRateShipperFacilities']
  stopType: IShipmentStopTypes
  unloadType: IFacilityScheduleRulesUnloadTypes
}

export const LaneFacilityDetails = memo<ILaneFacilityDetailsProps>(
  ({ dataTest, shipperRateShipperFacilities, stopType, unloadType }) => {
    const classes = useAwardedLaneFacilityDetailsStyles()

    const { shipperFacility } =
      shipperRateShipperFacilities.find(srsf => srsf.stopType === stopType) ?? {}
    const { facility, hours = [] } = shipperFacility ?? {}
    const { location, name } = facility ?? {}
    const { address, city, stateCode, zipcode } = location ?? {}

    const open247 = isFacilityOpen247(hours, stopType, unloadType)
    const shiftRows = open247 ? [] : getAwardedLaneFacilityShiftRows(hours, stopType, unloadType)
    const formattedName = titleCase(name) || DISPLAY.empty
    const formattedAddress = titleCase(address) || DISPLAY.empty
    const formattedCity = titleCase(city) || DISPLAY.empty
    const formattedStateCode = stateCode?.toUpperCase() || DISPLAY.empty
    const formattedPostalCode = zipcode || DISPLAY.empty

    return (
      <>
        {shipperFacility ? (
          <Grid className={classes.container} container direction='column'>
            <Grid item>
              <Typography data-test={`${dataTest}-location`} variant='body2'>
                {formattedName}
                <br />
                {formattedAddress}
                <br />
                {formattedCity},&nbsp;{formattedStateCode}&nbsp;{formattedPostalCode}
              </Typography>
            </Grid>

            <Grid container item>
              <Grid item xs={1}>
                <ClockIcon className={classes.clockIcon} size='large' />
              </Grid>

              <Grid data-test={`${dataTest}-hours`} container direction='column' item xs={11}>
                {open247 ? (
                  <Typography display='inline' variant='body2'>
                    Open 24/7
                  </Typography>
                ) : (
                  <>
                    {shiftRows.length > 0
                      ? shiftRows.map(({ label, value }) => (
                          <Grid
                            data-test={`${dataTest}-hours-${label}`}
                            key={label}
                            container
                            item
                            justifyContent='space-between'>
                            <Grid item>
                              <Typography className={classes.hoursText} variant='body2'>
                                {label}
                              </Typography>
                            </Grid>

                            <Grid item>
                              <Typography className={classes.hoursText} variant='body2'>
                                {value}
                              </Typography>
                            </Grid>
                          </Grid>
                        ))
                      : 'No Available Hours for Facility'}
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Typography>{DISPLAY.empty}</Typography>
        )}
      </>
    )
  }
)
